import React from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import WorkflowUserPicker from "./WorkflowUserPicker";
import {
  isFieldPersonSource,
  isManagerPersonSource,
  WORKFLOW_ENTERPRISE_DISCLAIMER,
  WORKFLOW_PERSON_SOURCES,
  WorkflowPersonSource,
} from "./workflowBuilderData";
import { useWorkflowReadOnly } from "./WorkflowReadOnlyContext";

export interface WorkflowPersonValue {
  source: WorkflowPersonSource | "";
  reference: string;
}

interface WorkflowPersonSourceFieldProps {
  /** Unique per instance; keys the select's label association. */
  idPrefix: string;
  sourceLabel: string;
  value: WorkflowPersonValue;
  onChange: (value: WorkflowPersonValue) => void;
  /** The approval picks "Specific user(s)" — several approvers. */
  multipleUsers?: boolean;
  /** Offered as the first option when nobody has to be named (the canvas owner). */
  noneLabel?: string;
  /** Under the source select, e.g. the owner hint. */
  helperText?: string;
}

/**
 * "Where does this person come from" — the shared control behind the canvas owner, the approver
 * source and the task assignee (Epic 80429). The reference that follows depends on the source: a
 * user picker for a specific user, the field key for the two field sources, nothing for the
 * creator sources. Manager sources add the Enterprise-vs-Portal disclaimer.
 */
const WorkflowPersonSourceField: React.FC<WorkflowPersonSourceFieldProps> = ({
  idPrefix,
  sourceLabel,
  value,
  onChange,
  multipleUsers = false,
  noneLabel,
  helperText,
}) => {
  const viewOnly = useWorkflowReadOnly();
  const labelId = `${idPrefix}-source-label`;

  // The reference means something different per source, so switching source clears it.
  const handleSourceChange = (event: SelectChangeEvent) =>
    onChange({ source: event.target.value as WorkflowPersonValue["source"], reference: "" });

  const handleReferenceChange = (reference: string) => onChange({ ...value, reference });

  return (
    <Box className="workflow-canvas-dialog__person-source">
      <Box className="workflow-canvas-dialog__person-source-row">
        <FormControl size="small" fullWidth disabled={viewOnly}>
          <InputLabel id={labelId}>{sourceLabel}</InputLabel>
          <Select
            labelId={labelId}
            label={sourceLabel}
            value={value.source}
            onChange={handleSourceChange}
            displayEmpty={Boolean(noneLabel)}
          >
            {noneLabel && <MenuItem value="">{noneLabel}</MenuItem>}
            {WORKFLOW_PERSON_SOURCES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.value === "specific-user" && multipleUsers ? "Specific user(s)" : option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {value.source === "specific-user" && (
          <WorkflowUserPicker
            label={multipleUsers ? "Approver user(s)" : "User"}
            value={value.reference}
            onChange={handleReferenceChange}
            multiple={multipleUsers}
            disabled={viewOnly}
            placeholder={multipleUsers ? "Pick one or more users" : "Pick a user"}
          />
        )}
        {isFieldPersonSource(value.source) && (
          <TextField
            label="Field"
            size="small"
            fullWidth
            disabled={viewOnly}
            value={value.reference}
            onChange={(event) => handleReferenceChange(event.target.value)}
            placeholder="The field the person comes from"
          />
        )}
      </Box>
      {helperText && <span className="workflow-canvas-dialog__person-source-hint">{helperText}</span>}
      {isManagerPersonSource(value.source) && (
        <Alert severity="info" className="workflow-canvas-dialog__disclaimer">
          {WORKFLOW_ENTERPRISE_DISCLAIMER}
        </Alert>
      )}
    </Box>
  );
};

export default WorkflowPersonSourceField;
